import { useEffect, useState } from 'react';

function useMegamenu(ref, menuItemsData, isMobile)
{
    const [menuState, setMenuState] = useState({
        menuAnimClass: "",
        firstLevelActive: false,
        secondLevelActive: false,
        thirdLevelActive: false,
        navigationTitle: "",
        firstMenuTitle: "",
        transitionClass: "",
        primaryLinksAnim: "",
        isSecondLevelVisible: false,
    });

    const showInitialWhenNoLinks = false; // TEMP

    const [featuredImageBlocks, setFeaturedImageBlocks]: any = useState([]);
    const [featuredArticles, setFeaturedArticles]: any = useState([]);

    const [firstMenuSubItem, setFirstMenuSubItem]: any = useState(null);
    const [hoveredLink, setHoveredLink]: any = useState(firstMenuSubItem);
    const [megaMenuRef, setMegaMenuRef]: any = useState(null);
    const [menuItems, setMenuItems]: any = useState(menuItemsData);


    const updatedMenuItems = menuItems?.subMenuItems?.[0]?.links?.map(link => {
        const titlePart = link.title?.split('|')[0].trim();
        return {
            ...link,
            title_without_classes: titlePart,
        };
    }) || [];

    const firstLevelLinks = updatedMenuItems || [];
    const defaultSecondLevelLinks = firstLevelLinks[0]?.links || [];
    const [secondLevelLinks, setSecondLevelLinks] = useState(defaultSecondLevelLinks);

    useEffect(() => {
        if (menuItems?.subMenuItems && menuItems.subMenuItems.length > 0) {
            if (!isMobile && menuItems.subMenuItems[0].links && menuItems.subMenuItems[0].links.length > 0) {
                const initMenuItem = menuItems.subMenuItems[0].links[0];
                setFirstMenuSubItem(initMenuItem);
                setHoveredLink(initMenuItem);
                setActiveLink(initMenuItem.handle);
            }
        }
    }, [menuItems, isMobile]);

    const updateMenuState = (updates) => setMenuState((prevState) => ({ ...prevState, ...updates }));

    const handleFirstLevelClick = (link) => {
        if (link.links && link.links.length > 0) {
            updateMenuState({
                navigationTitle: link.title,
                transitionClass: "slide-out-left",
                firstLevelActive: false,
                secondLevelActive: true,
                isSecondLevelVisible: true,
                primaryLinksAnim: "slide-in-from-right",
            });
            window.dispatchEvent(new Event("megamenu-menu-open"));
        }
    };

    const getPadding = (data) => {
        return `px-[${data.padding_left_right_mobile || 0}px]
        lg:px-[${data.padding_left_right_desktop || 0}px]
        pt-[${data.padding_top_mobile || 0}px]
        lg:pt-[${data.padding_top_desktop || 0}px]
        pb-[${data.padding_bottom_mobile || 0}px]
        lg:pb-[${data.padding_bottom_desktop || 0}px]`;
    }

    const getInitialFeaturedImageBlocks = () => {
        if (!menuItems.blocks) return [];
        
        const filterTopAreaBlocks = menuItems.blocks.filter((item) => {
            const matchesSearch = item.type
                .toLowerCase()
                .includes('toparea');
            return matchesSearch;
        });

        return filterTopAreaBlocks.reduce((initialBlocks, block) => {
            for (let i = 1; i <= 4; i++) {
                const imageKey = `image_${i}`;
                const mobileImageKey = `mobile_image_${i}`;
                const titleKey = `title_${i}`;
                const urlKey = `url_${i}`;
                const buttonTextKey = `button_text_${i}`;
                const buttonStyleKey = `button_text_style_${i}`;
                const buttonIcon = `icon_${i}`;

                if (block.settings[imageKey]) {
                    initialBlocks.push({
                        image: block.settings[imageKey],
                        mobile_image: block.settings[mobileImageKey],
                        title: block.settings[titleKey],
                        url: block.settings[urlKey],
                        button_text: block.settings[buttonTextKey],
                        layout: block.settings.button_layout,
                        button_style: block.settings[buttonStyleKey],
                        icon: block.settings[buttonIcon],
                    });
                }
            }

            return initialBlocks;
        }, []);
    };

    // Get featured articles for a specific category
    const getFeaturedArticles = (link = null) => {
        if (!menuItems.blocks) return [];

        const featuredBlocks = menuItems.blocks.filter((block) =>
            block.type.toLowerCase().includes('article')
        );

        const articles = [];
        
        featuredBlocks.forEach(block => {
            // Check if we're looking for a specific category and this block matches
            // Or if no link is specified (default articles)
            // Or if the block has no category (global article)
            if (!link || !block.settings.category || 
                (link.title_without_classes && block.settings.category === link.title_without_classes)) {
                // This is a matching article block or a general one
                if (block.settings.title) {
                    const article = {
                        title: block.settings.title,
                        description: block.settings.description,
                        image: block.settings.image,
                        url: block.settings.url,
                        linkText: block.settings.link_text || "Read More"
                    };
                    
                    articles.push(article);
                }
            }
        });

        return articles;
    };

    const updateFeaturedImageBlocks = (link) => {
        if (!menuItems.blocks) return;
        
        const filterImageBlocks = menuItems.blocks.filter((item) => {
            return item.type.toLowerCase().includes('images');
        });

        const updatedBlocks = filterImageBlocks?.reduce((blocks, block) => {
            if (block.settings.title === link?.title_without_classes) {
                for (let i = 1; i <= 4; i++) {
                    const imageKey = `image_${i}`;
                    const mobileImageKey = `mobile_image_${i}`;
                    const titleKey = `title_${i}`;
                    const urlKey = `url_${i}`;
                    const buttonTextKey = `button_text_${i}`;
                    const buttonStyleKey = `button_text_style_${i}`;
                    const buttonIcon = `icon_${i}`;

                    if (block.settings[imageKey]) {
                        blocks.push({
                            image: block.settings[imageKey],
                            mobile_image: block.settings[mobileImageKey],
                            title: block.settings[titleKey],
                            url: block.settings[urlKey],
                            button_text: block.settings[buttonTextKey],
                            layout: block.settings.button_layout,
                            button_style: block.settings[buttonStyleKey],
                            icon: block.settings[buttonIcon],
                        });
                    }
                }
            }
            return blocks;
        }, []) || [];
        
        setFeaturedImageBlocks(updatedBlocks);
        
        // Also update featured articles when link changes
        const articles = getFeaturedArticles(link);
        setFeaturedArticles(articles);
    };

    const handleBackClick = () => {
        if (menuState.secondLevelActive) {
            updateMenuState({
                transitionClass: "slide-in-left",
                secondLevelActive: false,
                navigationTitle: menuState.firstMenuTitle,
                firstLevelActive: true,
                primaryLinksAnim: "slide-in-left",
            });

            const drawerElement = document.querySelector(
                ".megamenu__secondary--links",
            );
            if (drawerElement) {
                const handleAnimationEnd = () => {
                    updateMenuState({ isSecondLevelVisible: false });
                    drawerElement.removeEventListener("animationend", handleAnimationEnd);
                };
                drawerElement.addEventListener("animationend", handleAnimationEnd);
            }
        } else {
            window.dispatchEvent(new Event("megamenu-menu-close"));
            updateMenuState({
                primaryLinksAnim: "slide-out-to-right",
                menuAnimClass: "slide-out-to-right",
                isFirstLevelActive: true,
            });
            const drawerElement = document.querySelector(".megamenu__primary--links");
            if (drawerElement) {
                const handleAnimationEnd = () => {
                    updateMenuState({ isSecondLevelVisible: false });
                    drawerElement.removeEventListener("animationend", handleAnimationEnd);
                };
                drawerElement.addEventListener("animationend", handleAnimationEnd);
            }
        }
    };

    const handleMenuClose = () => {
        updateMenuState({
            primaryLinksAnim: "slide-out-to-right",
            menuAnimClass: "slide-out-left",
            firstLevelActive: false,
            secondLevelActive: false,
            isSecondLevelVisible: false,
        });
    };

    function onLinkHover(link) {
        setHoveredLink(link);

        if (link?.level <= 2) {
            setActiveLink(link.handle);
            setSecondLevelLinks(link?.links || []);
            updateFeaturedImageBlocks(link);
        }

        if (showInitialWhenNoLinks) {
            // Check if we have category-specific featured images
            const filterImageBlocks = menuItems.blocks?.filter((item) => {
                const matchesSearch = item.type
                    .toLowerCase()
                    .includes('images');
                return matchesSearch;
            }) || [];
            
            let hasFeaturedImages = false;

            filterImageBlocks?.forEach((block) => {
                if (block.settings.title === link?.title_without_classes) {
                    hasFeaturedImages = true;
                }
            });

            const filterTopAreaBlocks = menuItems.blocks?.filter((item) => {
                const matchesSearch = item.type
                    .toLowerCase()
                    .includes('toparea');
                return matchesSearch;
            }) || [];
            
            // If no category-specific images, show initial featured images
            if (!hasFeaturedImages && filterTopAreaBlocks.length > 0) {
                setFeaturedImageBlocks(getInitialFeaturedImageBlocks());
            }
            
            // Update articles when link changes
            const articles = getFeaturedArticles(link);
            setFeaturedArticles(articles);
        }
    }

    const handleClickOutside = (event) => {
        const parentMenu = event?.target?.closest('[data-id]')?.getAttribute('data-id');
        
        const isClickInside = megaMenuRef?.current && megaMenuRef.current.contains(event.target)
            || parentMenu === window.Alpine?.store('navigation')?.getMenuId();

        if (!isClickInside) {
            // @ts-ignore
            window.Alpine?.store('navigation').setMenuId(null)
        }
    };

    // Function to add active class based on handle
    function setActiveLink(handle) {
        const activeLink = document.querySelector(
            ".megamenu__link-container.active",
        );

        if (activeLink) {
            activeLink.classList.remove("active");
        }

        const linkDiv = document.querySelector(`div[data-link-id='${handle}']`);
    
        if (linkDiv) {
            // Add the active class
            linkDiv.classList.add("active");
        }
    }

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        
        // Initial setup for articles and featured images
        const initialArticles = getFeaturedArticles();
        setFeaturedArticles(initialArticles);
        
        const initialImageBlocks = getInitialFeaturedImageBlocks();
        setFeaturedImageBlocks(initialImageBlocks);
        
        // Set megaMenuRef
        if (ref && ref.current) {
            setMegaMenuRef(ref);
        }
        
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [ref, menuItems]);

    return {
        menuState,
        menuItems,
        updateMenuState,
        handleFirstLevelClick,
        handleBackClick,
        handleMenuClose,
        onLinkHover,
        getPadding,
        hoveredLink,
        megaMenuRef,
        firstLevelLinks,
        secondLevelLinks,
        featuredImageBlocks,
        featuredArticles,
        setFeaturedImageBlocks
    };
}

export default useMegamenu;
